import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../utils/format";
import Toast from "../components/Toast";
import MemberAuthTab from "../components/tabs/MemberAuthTab";
import MemberPropertiesTab from "../components/tabs/MemberPropertiesTab";
import SettingsTab from "../components/tabs/SettingsTab";
import MemberModal from "../components/MemberModal";

const API_URL = "https://api.bizlingerwateradmin.co.kr";

const MainPage = () => {
  const [accessToken, setAccessToken] = useState(null);
  const [mallId, setMallId] = useState(null);
  const [shopNo, setShopNo] = useState(9999);
  const [shops, setShops] = useState([]);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const [shopSaving, setShopSaving] = useState(false);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("회원가입인증");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 회원가입인증 탭 데이터
  const [waitingMembers, setWaitingMembers] = useState([]);

  // 회원가입항목 탭 데이터 (MemberPropertiesTab에서 직접 관리)
  // const [groupNames, setGroupNames] = useState([]);
  // const [properties, setProperties] = useState([]);
  // const [readOnlySettings, setReadOnlySettings] = useState({});
  // const [unmodifiableSettings, setUnmodifiableSettings] = useState({});

  // 모달 관련 state
  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // 자동 새로고침 관련 state
  const [countdown, setCountdown] = useState(30);

  // 토스트 관련 state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const navigate = useNavigate();

  // shop_no 목록 불러오기
  const fetchShops = useCallback(async () => {
    console.log("fetchShops 호출됨, mallId:", mallId);
    setShopsLoading(true);
    try {
      const response = await fetch(`${API_URL}/shop-no/?mall_id=${mallId}`);
      console.log("API 응답 상태:", response.status);
      if (!response.ok) throw new Error("상점 목록을 불러오는데 실패했습니다.");
      const data = await response.json();
      console.log("API 응답 데이터:", data);
      setShops(data.shops || []);
      setShopNo(data.shop_no || 1);
      console.log("설정된 shops:", data.shops, "설정된 shopNo:", data.shop_no);
    } catch (err) {
      console.error("fetchShops 에러:", err);
      setError(err.message);
    } finally {
      setShopsLoading(false);
    }
  }, [mallId]);

  // 회원가입 대기자 목록 조회
  const fetchWaitingMembers = useCallback(async () => {
    if (shops.length === 0) return; // shop 목록이 없으면 데이터 불러오지 않음
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/signup/wait-list/?mall_id=${mallId}&shop_no=${shopNo}`,
      );
      if (!response.ok) throw new Error("데이터를 불러오는데 실패했습니다.");
      const data = await response.json();
      setWaitingMembers(data.members || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setCountdown(30); // 새로고침 후 카운트다운 리셋
    }
  }, [mallId, shopNo]);

  // 회원등급 목록 조회 - MemberPropertiesTab에서 직접 관리
  // const fetchGroupNames = useCallback(async () => {
  //   try {
  //     const response = await fetch(
  //       `${API_URL}/customer-groups/?mall_id=${mallId}&shop_no=${shopNo}`
  //     );
  //     if (!response.ok)
  //       throw new Error("회원등급 데이터를 불러오는데 실패했습니다.");
  //     const data = await response.json();
  //     setGroupNames(data.group_names || []);
  //   } catch (err) {
  //     setError(err.message);
  //   }
  // }, [mallId, shopNo]);

  // 회원가입항목 목록 조회 - MemberPropertiesTab에서 직접 관리
  // const fetchProperties = useCallback(async () => {
  //   try {
  //     const response = await fetch(
  //       `${API_URL}/customer-properties/?mall_id=${mallId}&shop_no=${shopNo}`
  //     );
  //     if (!response.ok)
  //       throw new Error("회원가입항목 데이터를 불러오는데 실패했습니다.");
  //     const data = await response.json();
  //     const filterPropertyKeys = [
  //       "member_id",
  //       "password",
  //       "cellphone",
  //       "company_member",
  //       "address2",
  //     ];
  //     const filteredProperties = data.properties.filter(
  //       (property) => !filterPropertyKeys.includes(property.key)
  //     );
  //     setProperties(filteredProperties || []);
  //     setUnmodifiableSettings(data.unmodifiable || {});
  //   } catch (err) {
  //     setError(err.message);
  //   }
  // }, [mallId, shopNo]);

  useEffect(() => {
    const token = localStorage.getItem("cafe24_access_token");
    const mall_id = localStorage.getItem("cafe24_mall_id");
    // const token = "Ie9DEc8kKyrXPQwxTqCvAG";
    // const mall_id = "lingteabiz";

    if (!token) {
      navigate("/");
      return;
    }

    setAccessToken(token);
    setMallId(mall_id);
  }, [navigate]);

  // mallId가 설정되면 shop 목록 불러오기
  useEffect(() => {
    if (mallId) {
      fetchShops();
    }
  }, [mallId, fetchShops]);

  useEffect(() => {
    if (mallId && activeTab === "회원가입인증") {
      fetchWaitingMembers();
    }
    // 등급별설정 탭 데이터는 MemberPropertiesTab에서 직접 관리
  }, [mallId, shopNo, activeTab, fetchWaitingMembers]);

  // shop_no 변경 저장
  const saveShopNo = async (newShopNo, originalShopNo) => {
    setShopSaving(true);
    try {
      const response = await fetch(`${API_URL}/shop-no/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mall_id: mallId,
          shop_no: newShopNo,
        }),
      });

      if (!response.ok) {
        throw new Error("상점 번호 저장에 실패했습니다.");
      }

      showToastMessage("상점 선택이 저장되었습니다.", "success");
    } catch (err) {
      // 오류 발생 시 이전 shopNo로 되돌리기
      setShopNo(originalShopNo);
      showToastMessage(
        `상점 선택 저장 중 오류가 발생했습니다: ${err.message}`,
        "error",
      );
    } finally {
      setShopSaving(false);
    }
  };

  // shop_no 변경 핸들러
  const handleShopChange = (newShopNo) => {
    setShopDropdownOpen(false);
    if (newShopNo !== shopNo) {
      // 낙관적 업데이트: 즉시 UI 반영
      const originalShopNo = shopNo;
      setShopNo(newShopNo);
      saveShopNo(newShopNo, originalShopNo);
    }
  };

  // 자동 새로고침 타이머
  useEffect(() => {
    if (activeTab === "회원가입인증" && mallId) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            fetchWaitingMembers();
            return 30; // 카운트다운 리셋
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setCountdown(30); // 다른 탭으로 이동시 카운트다운 리셋
    }
  }, [activeTab, mallId, fetchWaitingMembers]);

  // shop 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shopDropdownOpen && !event.target.closest(".shop-dropdown-wrap")) {
        setShopDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [shopDropdownOpen]);

  // unmodifiable 설정 초기화 - MemberPropertiesTab에서 직접 관리
  // useEffect(() => {
  //   if (
  //     groupNames.length > 0 &&
  //     properties.length > 0 &&
  //     Object.keys(unmodifiableSettings).length > 0
  //   ) {
  //     const initialReadOnlySettings = {};

  //     groupNames.forEach((groupName) => {
  //       properties.forEach((property) => {
  //         const key = `${groupName}_${property.key}`;
  //         // 해당 그룹의 unmodifiable 설정에 이 property가 포함되어 있는지 확인
  //         initialReadOnlySettings[key] =
  //           unmodifiableSettings[groupName]?.includes(property.key) || false;
  //       });
  //     });
  //     console.log("initialReadOnlySettings:", initialReadOnlySettings);

  //     setReadOnlySettings(initialReadOnlySettings);
  //   }
  // }, [groupNames, properties, unmodifiableSettings]);

  // 토스트 관련 함수
  const showToastMessage = useCallback((message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  }, []);

  const hideToast = () => {
    setShowToast(false);
  };

  // 수정불가 설정 변경 - MemberPropertiesTab에서 직접 관리
  // const handleReadOnlyChange = async (groupName, propertyKey, isChecked) => {
  //   // 즉시 UI 업데이트 (낙관적 업데이트)
  //   setReadOnlySettings((prev) => ({
  //     ...prev,
  //     [`${groupName}_${propertyKey}`]: isChecked,
  //   }));

  //   try {
  //     const response = await fetch(`${API_URL}/setting/unmodifiable/`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({
  //         mall_id: mallId,
  //         shop_no: shopNo,
  //         group_name: groupName,
  //         property_key: propertyKey,
  //       }),
  //     });

  //     if (!response.ok) {
  //       throw new Error("설정 변경에 실패했습니다.");
  //     }

  //     const data = await response.json();

  //     if (data.updated) {
  //       // 서버에서 받은 최신 unmodifiable 상태를 로컬 state에 반영
  //       setUnmodifiableSettings((prev) => ({
  //         ...prev,
  //         [groupName]: data.unmodifiable || [],
  //       }));

  //       // 해당 그룹의 모든 설정을 서버 응답에 맞게 업데이트
  //       setReadOnlySettings((prev) => {
  //         const updated = { ...prev };
  //         properties.forEach((property) => {
  //           const key = `${groupName}_${property.key}`;
  //           updated[key] = data.unmodifiable.includes(property.key);
  //         });
  //         return updated;
  //       });

  //       // 성공 토스트 표시
  //       showToastMessage("설정 변경이 저장되었습니다.", "success");
  //     }
  //   } catch (err) {
  //     // API 호출 실패 시 UI를 원래 상태로 되돌림
  //     setReadOnlySettings((prev) => ({
  //       ...prev,
  //       [`${groupName}_${propertyKey}`]: !isChecked,
  //     }));

  //     // 에러 토스트 표시
  //     showToastMessage(
  //       `설정 변경 중 오류가 발생했습니다: ${err.message}`,
  //       "error"
  //     );
  //   }
  // };

  // const handleLogout = () => {
  //   localStorage.removeItem("cafe24_access_token");
  //   localStorage.removeItem("cafe24_mall_id");
  //   navigate("/");
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  //const tabs = ["회원가입인증", "회원가입항목", "알림설정"];
  const tabs = ["회원가입인증", "등급별설정"];
  if (!accessToken) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <h3>🔄 인증 정보를 확인하는 중...</h3>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* 헤더 */}
      <header
        style={{
          padding: "12px 0",
          borderBottom: "1px solid #ededed",
        }}
      >
        <div style={{ margin: "0 auto", padding: "0 20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <img
              src="/lingtea_biz_tmp_logo.png"
              alt="로고"
              width="120"
              height="60"
            ></img>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "131px",
                marginRight: "8px",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Shop 선택 콤보박스 */}
              <div
                className="shop-dropdown-wrap"
                style={{ position: "relative" }}
              >
                <div
                  className={`shop-select ${shopDropdownOpen ? "open" : ""}`}
                  onClick={() => setShopDropdownOpen(!shopDropdownOpen)}
                  style={{
                    minWidth: "160px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    background: "#fff",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#0170FF",
                    cursor: "pointer",
                    boxShadow: "0 0 0 1px #CFE5FF inset",
                    transition: "box-shadow .2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {(shopSaving || shopsLoading) && (
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid #CFE5FF",
                          borderTop: "2px solid #0170FF",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                    )}
                    {shopsLoading
                      ? ""
                      : (() => {
                          const foundShop = shops.find(
                            (shop) => Number(shop.shop_no) === Number(shopNo),
                          );
                          return foundShop?.shop_name || `Shop ${shopNo}`;
                        })()}
                  </div>
                  <span
                    style={{
                      fontSize: "10px",
                      transition: "transform 0.2s",
                      transform: shopDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  >
                    ▼
                  </span>
                </div>
                <ul
                  className={`shop-options ${shopDropdownOpen ? "open" : ""}`}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: "0",
                    right: "0",
                    padding: "8px 0",
                    listStyle: "none",
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                    zIndex: 50,
                    maxHeight: "150px",
                    overflowY: "auto",
                    visibility: shopDropdownOpen ? "visible" : "hidden",
                    opacity: shopDropdownOpen ? 1 : 0,
                    transform: shopDropdownOpen
                      ? "translateY(0)"
                      : "translateY(-8px)",
                    transition: "opacity .15s ease, transform .15s ease",
                    pointerEvents: shopDropdownOpen ? "auto" : "none",
                    margin: 0,
                  }}
                >
                  {shops.map((shop) => (
                    <li
                      key={shop.shop_no}
                      onClick={() => {
                        if (Number(shop.shop_no) === 1) {
                          handleShopChange(shop.shop_no);
                        }
                      }}
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        cursor:
                          Number(shop.shop_no) === 1
                            ? "pointer"
                            : "not-allowed",
                        color:
                          Number(shop.shop_no) === 1
                            ? Number(shopNo) === Number(shop.shop_no)
                              ? "#0170FF"
                              : "#222"
                            : "#9ca3af",
                        fontWeight:
                          Number(shopNo) === Number(shop.shop_no)
                            ? "600"
                            : "400",
                        transition: "background .15s",
                        opacity: Number(shop.shop_no) === 1 ? 1 : 0.5,
                      }}
                      onMouseEnter={(e) => {
                        if (Number(shop.shop_no) === 1) {
                          e.target.style.background = "#F0F7FF";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (Number(shop.shop_no) === 1) {
                          e.target.style.background = "transparent";
                        }
                      }}
                    >
                      {shop.shop_name}
                    </li>
                  ))}
                </ul>
              </div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity: 0.9,
                  //backgroundColor: "#F0F7FF",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  color: "#0170FF",
                }}
              >
                스토어 ID: {mallId}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main style={{ display: "flex", minHeight: "calc(100vh - 85px)" }}>
        {/* 왼쪽 사이드바 (탭 네비게이션) */}
        <aside
          style={{
            width: "240px",
            backgroundColor: "white",
            borderRight: "1px solid #e2e8f0",
            padding: "30px 0",
          }}
        >
          <div style={{ padding: "0 20px" }}>
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  width: "100%",
                  padding: "16px 20px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  backgroundColor:
                    activeTab === tab ? "#4A90E2" : "transparent",
                  color: activeTab === tab ? "white" : "#666",
                  textAlign: "left",
                  marginBottom: "8px",
                  boxShadow:
                    activeTab === tab
                      ? "0 4px 12px rgba(74,144,226,0.3)"
                      : "none",
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </aside>

        {/* 오른쪽 컨텐츠 영역 */}
        <div
          style={{
            flex: 1,
            padding: "30px",
            backgroundColor: "#f8fafc",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* 에러 메시지 */}
          {error && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "20px",
                color: "#991b1b",
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* 회원가입인증 탭 */}
          {activeTab === "회원가입인증" && (
            <MemberAuthTab
              loading={loading}
              waitingMembers={waitingMembers}
              countdown={countdown}
              fetchWaitingMembers={fetchWaitingMembers}
              setSelectedMember={setSelectedMember}
              setShowModal={setShowModal}
              formatDate={formatDate}
            />
          )}

          {/* 회원가입항목 탭 */}
          {activeTab === "등급별설정" && (
            <MemberPropertiesTab
              mallId={mallId}
              shopNo={shopNo}
              showToastMessage={showToastMessage}
              API_URL={API_URL}
            />
          )}

          {/* 설정 탭 */}
          {activeTab === "알림설정" && (
            <SettingsTab
              mallId={mallId}
              shopNo={shopNo}
              showToastMessage={showToastMessage}
              API_URL={API_URL}
            />
          )}

          {/* 이전 탭 내용들 완전 제거 */}
        </div>
      </main>

      {/* 회원 상세 정보 모달 */}
      <MemberModal
        showModal={showModal}
        selectedMember={selectedMember}
        mallId={mallId}
        shopNo={shopNo}
        onClose={() => {
          setShowModal(false);
          setSelectedMember(null);
        }}
        formatPhoneNumber={formatPhoneNumber}
      />

      {/* 이전 모달 내용 완전 제거 */}

      {/* 토스트 */}
      <Toast
        show={showToast}
        message={toastMessage}
        type={toastType}
        onClose={hideToast}
      />
    </div>
  );
};

// 테이블 스타일은 MemberPropertiesTab에서 관리

export default MainPage;
